import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomCheckbox from '../../../shared-components/custom-check-box/CustomCheckBox';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomSwitchButton from '../../../shared-components/custom-switch-button/custom-switch-button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import MultiSelectDropdown from '../../../shared-components/multi-select-field/MultiSelectField';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import ConvertDDMMYYYYtoISO from '../../../shared-functions/ConvertDDMMYYYYtoISO';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { changeDateFormat } from '../../../shared-functions/DateTime';
import { stringValidation } from '../../../shared-functions/Validation';
import PenaltyManagementApi from '../../../utils/api/penalty-management';

interface UserAddInterface {
  ceilingType: string;
  ceilingValue: number;
  triggerMessage: string[];
  effectiveFrom: string;
  effectiveEnd: string;
  status: boolean;
  pricing: PricingInterface;
  rules: RuleInterface[];
}

interface PricingInterface {
  applyAll: boolean;
  base: boolean;
  peak: boolean;
  offpeak: boolean;
}
interface RuleInterface {
  from: string;
  to: string;
  mode: string;
  amount: string;
}

/**
 * get Dynamic Pricing Array
 * @param {any} pricing - The pricing interface object
 * @returns {any} Array of selected pricing types
 */
const getDynamicPricingArray = (pricing: PricingInterface): string[] => {
  const result: string[] = [];

  if (pricing.base) result.push('base');
  if (pricing.peak) result.push('peak');
  if (pricing.offpeak) result.push('offpeak');

  return result;
};

/**
 * Checks whether the "From (Mins)" value is invalid
 * @param {any} rule - Rule object containing time range values
 * @returns {any} true if "from" is empty or negative
 */
const isFromInvalid = (rule: RuleInterface): boolean => rule.from === '' || Number(rule.from) < 0;

/**
 * Checks whether the "To (Mins)" value is invalid
 * @param {any} rule - Rule object containing time range values
 * @returns {any} true if "to" is empty or less than "from"
 */
const isToInvalid = (rule: RuleInterface): boolean =>
  rule.to === '' || Number(rule.to) < Number(rule.from);

/**
 * Checks whether the "Mode" field is invalid
 * @param {any} rule - Rule object containing mode information
 * @returns {any} true if mode is not selected
 */
const isModeInvalid = (rule: RuleInterface): boolean => rule.mode === '';

/**
 * Checks whether the "Amount" field is invalid
 * @param {any} rule - Rule object containing amount value
 * @returns {any} true if amount is empty
 */
const isAmountInvalid = (rule: RuleInterface): boolean => rule.amount === '';

/**
 * Normalize a value to an array
 * @param {any} value - string or string array
 * @returns {any} - array of strings
 */
const normalizeToArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (value) return [value];
  return [];
};

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}
/**
 * Global Setting Component
 * @param {any} param0 - The param0
 * @param {any} param0.closeModal - The closeModal prop
 * @param {any} param0.functionCall - The functionCall prop
 */
function GlobalSetting({
  closeModal,
  functionCall,
}: {
  closeModal: (value: string) => void;
  functionCall: () => void;
}) {
  const { t, i18n } = useTranslation('penaltyManagement');
  const dispatch = useAppDispatch();
  const [buttonStatus, setButtonStatus] = useState(false);
  const [ruleErrors, setRuleErrors] = useState<{
    [index: number]: { from: boolean; to: boolean };
  }>({});
  const [errorMessage, setErrorMessage] = useState(false);
  const [penaltyDetails, setPenaltyDetails] = useState<UserAddInterface>({
    ceilingType: '',
    ceilingValue: 0,
    triggerMessage: [],
    effectiveFrom: '',
    effectiveEnd: '',
    status: false,
    pricing: { applyAll: false, base: false, peak: false, offpeak: false },
    rules: [{ from: '0', to: '', mode: '', amount: '' }],
  });
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  /**
   * Close modal function
   */
  const handleClose = () => {
    setModalBoxStatus('close');
    functionCall();
    closeModal('');
  };

  /**
   * Show success notification
   * @param {string} notificationTitle - Notification title
   */
  const showSuccess = (notificationTitle: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <SuccessNotification
          title={notificationTitle}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={handleClose} // ✅ close + refresh here
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Normal Input Handler
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | boolean | string[]) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Handle Pricing Checkbox Changes
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handlePricingChange = (name: keyof PricingInterface, value: boolean) => {
    setPenaltyDetails((previous) => {
      const updatedPricing = { ...previous.pricing, [name]: value };

      // Auto activate applyAll when all 3 are true
      updatedPricing.applyAll =
        updatedPricing.base && updatedPricing.peak && updatedPricing.offpeak;
      return {
        ...previous,
        pricing: updatedPricing,
      };
    });
  };

  /**
   * Apply All Switch
   * @param {any} checked - The checked
   */
  const handleApplyAllChange = (checked: boolean) => {
    setPenaltyDetails((previous: any) => ({
      ...previous,
      pricing: {
        applyAll: checked,
        base: checked,
        peak: checked,
        offpeak: checked,
      },
    }));
  };

  const allModes = [
    { label: t('grace'), value: 'grace' },
    { label: t('flatRate'), value: 'flatRate' },
    { label: t('perMinute'), value: 'perMinute' },
  ];

  /**
   * Add rule row
   */
  const addRule = () => {
    const index = penaltyDetails.rules.length - 1;
    const lastRule = penaltyDetails.rules[index];
    const from = Number(lastRule.from);
    const to = Number(lastRule.to);
    let fromError = false;
    let toError = false;

    // Case 1: Empty values
    if (lastRule.from === '') {
      fromError = true;
    }

    if (lastRule.to === '') {
      toError = true;
    }

    if (lastRule.to === '' || to <= from) {
      toError = true;
    }
    // Case 2: Range invalid
    if (lastRule.from !== '' && lastRule.to !== '' && to < from) {
      fromError = true;
      toError = true;
    }

    // If any error → show error only for this row
    if (fromError || toError) {
      setRuleErrors((previous) => ({
        ...previous,
        [index]: { from: fromError, to: toError },
      }));
      return;
    }

    // Clear error if valid
    setRuleErrors((previous) => ({
      ...previous,
      [index]: { from: false, to: false },
    }));

    // If valid → create new rule
    setPenaltyDetails((previous) => ({
      ...previous,
      rules: [
        ...previous.rules,
        {
          from: String(to), // next rule starts from previous TO
          to: '',
          mode: '',
          amount: '',
        },
      ],
    }));
  };

  /**
   * Delete rule row
   * @param {any} index - The index
   */
  const deleteRule = (index: number) => {
    setPenaltyDetails((previous) => ({
      ...previous,
      rules: previous.rules.filter((_, input) => input !== index),
    }));
  };

  /**
   * Update rule row field value
   * @param {any} index - The index
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const updateRule = (index: number, name: keyof RuleInterface, value: string) => {
    const rules = [...penaltyDetails.rules];

    // Update field
    rules[index][name] = value;

    // ✅ If mode is GRACE → force amount = "0"
    if (name === 'mode' && value === 'grace') {
      rules[index].amount = '0';
    }

    // ✅ If switching away from grace → clear amount
    if (name === 'mode' && value !== 'grace') {
      rules[index].amount = '';
    }

    setPenaltyDetails((previous) => ({
      ...previous,
      rules,
    }));
  };

  /**
   * Filter available mode options based on already selected modes
   * @param {any} currentIndex - The currentIndex
   */
  const getAvailableModes = (currentIndex: number) => {
    const selectedModes = penaltyDetails.rules.map((r) => r.mode).filter(Boolean);

    return allModes.filter((m) => {
      // Allow any mode if it's already selected for the current index (editing case)
      if (penaltyDetails.rules[currentIndex].mode === m.value) return true;
      // Make "Grace" unique: if already selected anywhere else, hide it
      if (m.value === 'grace' && selectedModes.includes('grace')) {
        return false;
      }

      // Allow Flat Rate & Per Minute multiple times
      return true;
    });
  };

  /**
   * Rules Valid
   */
  const isRulesValid = (): boolean => {
    let valid = true;
    const errors: {
      [index: number]: { from: boolean; to: boolean };
    } = {};

    penaltyDetails.rules.forEach((rule, index) => {
      const from = Number(rule.from);
      const to = Number(rule.to);

      let fromError = false;
      let toError = false;

      // 1️⃣ Empty checks
      if (rule.from === '') fromError = true;
      if (rule.to === '') toError = true;

      // 2️⃣ Same value (10–10)
      if (rule.from !== '' && rule.to !== '' && from === to) {
        toError = true;
      }

      // 3️⃣ to < from
      if (rule.from !== '' && rule.to !== '' && to < from) {
        fromError = true;
        toError = true;
      }

      if (index > 0) {
        const previousTo = Number(penaltyDetails.rules[index - 1].to);

        if (rule.from !== '' && !Number.isNaN(previousTo) && from !== previousTo) {
          fromError = true; // ❌ breaks chain
        }
      }

      if (fromError || toError) {
        valid = false;
        errors[index] = { from: fromError, to: toError };
      }

      // 5️⃣ Mode validation
      if (!rule.mode) valid = false;

      // 6️⃣ Amount validation
      if (rule.mode !== 'grace' && rule.amount === '') valid = false;
    });

    setRuleErrors(errors);
    return valid;
  };

  const pricingSelected =
    penaltyDetails.pricing.base || penaltyDetails.pricing.peak || penaltyDetails.pricing.offpeak;

  const isDateInvalid =
    penaltyDetails.effectiveFrom &&
    penaltyDetails.effectiveEnd &&
    new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd));
  /**
   * Submit payload to API
   */
  const handleSubmit = async () => {
    setButtonStatus(true);
    if (
      penaltyDetails.ceilingType === '' ||
      !stringValidation(1, 100, penaltyDetails.ceilingType) ||
      penaltyDetails.ceilingValue === 0 ||
      penaltyDetails.triggerMessage.length === 0 ||
      penaltyDetails.effectiveFrom === '' ||
      penaltyDetails.effectiveEnd === '' ||
      new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom)) >
        new Date(ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd)) ||
      !pricingSelected ||
      !isRulesValid()
    ) {
      setErrorMessage(true);
      setButtonStatus(false);
      return;
    }

    try {
      const payload = {
        ceilingType: penaltyDetails.ceilingType,
        ceilingValue: penaltyDetails.ceilingValue,
        triggerMessage: penaltyDetails.triggerMessage,
        effectiveFrom: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveFrom),
        effectiveTo: ConvertDDMMYYYYtoISO(penaltyDetails.effectiveEnd),
        dynamicPricingAll: penaltyDetails.pricing.applyAll,
        dynamicPricing: getDynamicPricingArray(penaltyDetails.pricing),
        durationAmountList: penaltyDetails.rules,
        status: penaltyDetails.status,
      };
      await PenaltyManagementApi.createGlobalPenalty(payload);
      showSuccess(t('globalSettingsUpdatedSuccess'));
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    } finally {
      setButtonStatus(false);
    }
  };

  /**
   * Go back
   */
  const handleCancel = () => {
    closeModal('');
  };

  useEffect(() => {
    /**
     * fetchOverrideDetails
     */
    const fetchOverrideDetails = async () => {
      try {
        const response = await PenaltyManagementApi.globalSettingQuickView();
        const data = response?.data || {};

        const dynamicPricingArray = normalizeToArray(data.dynamicPricing);

        setPenaltyDetails({
          ceilingType: data.ceilingType ?? '',
          ceilingValue: data.ceilingValue ?? 0,
          triggerMessage: normalizeToArray(data.triggerMessage),
          effectiveFrom: ConvertUTCtoDate(data.effectiveFrom) ?? '',
          effectiveEnd: ConvertUTCtoDate(data.effectiveTo) ?? '',
          status: Boolean(data.status),

          pricing: {
            applyAll: Boolean(data.dynamicPricingAll),
            base: dynamicPricingArray.includes('base'),
            peak: dynamicPricingArray.includes('peak'),
            offpeak: dynamicPricingArray.includes('offpeak'),
          },

          rules:
            Array.isArray(data.durationAmountList) && data.durationAmountList.length
              ? data.durationAmountList.map((item: any) => ({
                  from: String(item.from),
                  to: String(item.to),
                  mode: item.mode,
                  amount: String(item.amount),
                }))
              : [{ from: '', to: '', mode: '', amount: '' }],
        });
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: error?.response?.status || 500,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      }
    };

    fetchOverrideDetails();
  }, []);

  return (
    <div className="global-setting-wrapper" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />{' '}
      <div>
        <PageTitle title={t('globalSettings')} />
      </div>
      <div className="global-setting-content">
        {/* Penalty Feature */}
        <div className="switch-row">
          <CustomSwitchButton
            checked={penaltyDetails.status}
            change={(checked) => handleInputChange('status', checked)}
          />
          <span>{t('penaltyFeature')}</span>
        </div>

        <div className="section-box">
          <div className="Ceiling-box">
            {/* Ceiling Type */}
            <SelectFiled
              name="ceilingType"
              inputLabel={t('ceilingType')}
              selectedValue={penaltyDetails.ceilingType}
              placeholder={t('selectOption')}
              onChangeValue={handleInputChange}
              valueRequired
              error={errorMessage && penaltyDetails.ceilingType === ''}
              errorMessage={t('ceilingTypeRequired')}
              options={[
                { label: t('fixedAmount'), value: 'fixedAmount' },
                { label: t('percentage'), value: 'percentage' },
              ]}
            />
            {/* Max Ceiling */}
            <CustomInput
              name="ceilingValue"
              inputLabel={t('maxCeilingValue')}
              type="number"
              valueRequired
              placeholder={t('enterValue')}
              value={penaltyDetails.ceilingValue}
              onChangeValue={handleInputChange}
              error={errorMessage && penaltyDetails.ceilingValue <= 0}
              errorMessage={t('maxCeilingValueRequired')}
            />
          </div>

          <div className="Trigger-Date-box">
            {/* Trigger Message */}
            <MultiSelectDropdown
              name="triggerMessage"
              title={t('triggerMessage')}
              selectedValues={penaltyDetails.triggerMessage}
              onSelectionChange={handleInputChange}
              placeholder={t('selectOption')}
              placeholderFilled={t('options')}
              options={[
                { label: t('suspendedEVSE'), value: 'SuspendedEVSE' },
                { label: t('suspendedEV'), value: 'SuspendedEV' },
                { label: t('faulted'), value: 'Faulted' },
                { label: t('finishing'), value: 'Finishing' },
              ]}
              search={false}
              multiSelectDisable={false}
              valueRequired
              fontSize="small"
              selectedChips
              error={errorMessage && penaltyDetails.triggerMessage.length === 0}
              errorMessage={t('triggerMessageRequired')}
            />
            {/* Date Range */}
            <div className="date-box-content">
              <CustomDatePicker
                inputLabel={t('startDate')}
                value={penaltyDetails.effectiveFrom}
                name="effectiveFrom"
                valueRequired
                onChangeValue={handleInputChange}
                placeholder={t('inputPlaceholder')}
                error={Boolean(
                  errorMessage && (penaltyDetails.effectiveFrom === '' || isDateInvalid),
                )}
                errorMessage={
                  penaltyDetails.effectiveFrom === ''
                    ? t('startDateRequired')
                    : t('startDateGreaterError')
                }
                minDate={changeDateFormat(new Date().toString())}
              />
              <CustomDatePicker
                inputLabel={t('endDate')}
                value={penaltyDetails.effectiveEnd}
                name="effectiveEnd"
                onChangeValue={handleInputChange}
                valueRequired
                placeholder={t('inputPlaceholder')}
                error={Boolean(
                  errorMessage && (penaltyDetails.effectiveEnd === '' || isDateInvalid),
                )}
                errorMessage={
                  penaltyDetails.effectiveEnd === '' ? t('endDateRequired') : t('endDateLessError')
                }
                minDate={changeDateFormat(new Date().toString())}
              />
            </div>
          </div>

          {/* Pricing Section */}
          <div className="apply-pricing-content">
            <div className="swith-content">
              <div className="switch-row">
                <CustomSwitchButton
                  checked={penaltyDetails.pricing.applyAll}
                  change={handleApplyAllChange}
                />
                <span>{t('applyToAll')}</span>
              </div>

              {/* Dynamic Pricing */}
              <p className="sub-title">
                {t('dynamicPricingList')} <span className="erro">*</span>
              </p>
              {/* Show only when applyAll=false */}
              {!penaltyDetails.pricing.applyAll && (
                <div className="checkbox-row">
                  <div className="checkbox">
                    <CustomCheckbox
                      checkboxLabel={t('base')}
                      name="base"
                      value={penaltyDetails.pricing.base}
                      onChangeValue={(_, value) => handlePricingChange('base', value as boolean)}
                      error={errorMessage && !pricingSelected}
                      errorMessage={t('baseRequired')}
                    />
                  </div>
                  <div className="checkbox">
                    <CustomCheckbox
                      checkboxLabel={t('peak')}
                      name="peak"
                      value={penaltyDetails.pricing.peak}
                      onChangeValue={(_, value) => handlePricingChange('peak', value as boolean)}
                      error={errorMessage && !pricingSelected}
                      errorMessage={t('peakRequired')}
                    />
                  </div>
                  <div className="checkbox">
                    <CustomCheckbox
                      checkboxLabel={t('offPeak')}
                      name="offPeak"
                      value={penaltyDetails.pricing.offpeak}
                      onChangeValue={(_, value) => handlePricingChange('offpeak', value as boolean)}
                      error={errorMessage && !pricingSelected}
                      errorMessage={t('offPeakRequired')}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RULE SECTION */}
          <div className="rule-box-content">
            {penaltyDetails.rules.map((rule, index) => (
              <React.Fragment key={index}>
                <div className="rule-box">
                  {/* FROM */}
                  <CustomInput
                    name="from"
                    inputLabel={t('fromMins')}
                    placeholder={t('fromMins')}
                    type="number"
                    value={rule.from}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof RuleInterface, String(value))
                    }
                    error={ruleErrors[index]?.from || (errorMessage && isFromInvalid(rule))}
                    errorMessage={t('fromRequired')}
                  />
                  {/* TO */}
                  <CustomInput
                    name="to"
                    inputLabel={t('toMins')}
                    placeholder={t('toMins')}
                    type="number"
                    value={rule.to}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof RuleInterface, String(value))
                    }
                    error={ruleErrors[index]?.to || (errorMessage && isToInvalid(rule))}
                    errorMessage={t('required')}
                  />
                  {/* MODE */}
                  <SelectFiled
                    name="mode"
                    inputLabel={t('mode')}
                    placeholder={t('selectOption')}
                    selectedValue={rule.mode}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof RuleInterface, String(value))
                    }
                    options={getAvailableModes(index)}
                    error={errorMessage && isModeInvalid(rule)}
                    errorMessage={t('modeRequired')}
                  />
                  <CustomInput
                    name="amount"
                    inputLabel={t('amount')}
                    placeholder={t('enterValue')}
                    type="number"
                    value={rule.amount}
                    onChangeValue={(name, value) =>
                      updateRule(index, name as keyof RuleInterface, String(value))
                    }
                    error={errorMessage && isAmountInvalid(rule)}
                    errorMessage={t('amountRequired')}
                    disabled={rule.mode === 'grace'}
                  />

                  {/* Delete Button */}
                  <div className="delete-button">
                    {penaltyDetails.rules.length > 1 && (
                      <MyButton
                        label=""
                        svgIcon={<DeleteIcon />}
                        buttonType="error"
                        onClickFunc={() => deleteRule(index)}
                        disableButton={buttonStatus}
                      />
                    )}
                  </div>
                </div>

                {/* Add Button ONLY for last row */}
                {index === penaltyDetails.rules.length - 1 && (
                  <div className="add-button">
                    <MyButton
                      label={t('add')}
                      svgIcon={<PlusIcon />}
                      buttonType="primary-outline-none"
                      onClickFunc={addRule}
                      disableButton={buttonStatus}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="button-row">
          <MyButton
            label={t('cancel')}
            buttonType="submit"
            onClickFunc={handleCancel}
            disableButton={buttonStatus}
          />
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            loading={buttonStatus}
          />
        </div>
      </div>
    </div>
  );
}

export default GlobalSetting;
